/**
 * Card 354 — the one definition of the TipDirect navigation.
 *
 * The desktop rail (TipDirectLeftMenu) and the mobile menu (MobileNavBar) used
 * to be two hand-maintained lists on two different `TapToTipTabs` enums. A tab
 * added to one silently never reached the other: Product Improvement, Tour
 * Photos, Feedback QR and Guest Feedback all shipped desktop-only first, and
 * Learn, Messaging and Stripe were still missing from phones when this landed.
 *
 * Now both menus render from TIPDIRECT_TABS below. Each entry carries its name,
 * path, labels, icons and gate. Where the two menus deliberately differ, the
 * entry says so (`desktopOnly`, `mobileOnly`, or a `mobile:` gate override)
 * instead of the difference being an absence from one list. A new tab added
 * here appears on both menus, under the same gate, unless it opts out.
 *
 * What is NOT here: each menu's markup and styling. Those stay in the menu
 * components, so moving a tab definition cannot change how a menu looks.
 */
import BookingsIcon from '@assets/images/Bookings.svg';
import ManifestIcon from '@assets/images/Manifest.svg';
import MobileSettingsIcon from '@assets/images/MobileSettings.svg';
import MobileBalanceIcon from '@assets/images/menu_icons/Balance.svg';
import MobileDashboardIcon from '@assets/images/menu_icons/Dashboard.svg';
import MobileFeedbackQrIcon from '@assets/images/menu_icons/FeedbackQr.svg';
import MobileGuestFeedbackIcon from '@assets/images/menu_icons/GuestFeedback.svg';
import MobilePaymentsIcon from '@assets/images/menu_icons/Payments.svg';
import MobileProfileIcon from '@assets/images/menu_icons/Profile.svg';
import MobileResourcesIcon from '@assets/images/menu_icons/Resources.svg';
import MobileReviewsIcon from '@assets/images/menu_icons/Reviews.svg';
import MobileStripeIcon from '@assets/images/menu_icons/Settings.svg';
import MobileSetupIcon from '@assets/images/menu_icons/Setup.png';
import MobileTipDirectIcon from '@assets/images/menu_icons/TipDirect.svg';
import BalanceActive from '@assets/images/tip_direct_nav_menu/Balance_Active.svg';
import BalanceStatic from '@assets/images/tip_direct_nav_menu/Balance_Static.svg';
import DashboardActive from '@assets/images/tip_direct_nav_menu/Dashboard_Active.svg';
import DashboardStatic from '@assets/images/tip_direct_nav_menu/Dashboard_Static.svg';
import FeedbackQrActive from '@assets/images/tip_direct_nav_menu/FeedbackQr_Active.svg';
import FeedbackQrStatic from '@assets/images/tip_direct_nav_menu/FeedbackQr_Static.svg';
import GuestFeedbackActive from '@assets/images/tip_direct_nav_menu/GuestFeedback_Active.svg';
import GuestFeedbackStatic from '@assets/images/tip_direct_nav_menu/GuestFeedback_Static.svg';
import GuestReviewsActive from '@assets/images/tip_direct_nav_menu/GuestReviews_Active.svg';
import GuestReviewsStatic from '@assets/images/tip_direct_nav_menu/GuestReviews_Static.svg';
import LearnActive from '@assets/images/tip_direct_nav_menu/Learn_Active.svg';
import LearnStatic from '@assets/images/tip_direct_nav_menu/Learn_Static.svg';
import MessagingActive from '@assets/images/tip_direct_nav_menu/Messaging_ActiveV2.svg';
import MessagingStatic from '@assets/images/tip_direct_nav_menu/Messaging_StaticV2.svg';
import PaymentsActive from '@assets/images/tip_direct_nav_menu/Payments_Active.svg';
import PaymentsStatic from '@assets/images/tip_direct_nav_menu/Payments_Static.svg';
import ProductImprovementActive from '@assets/images/tip_direct_nav_menu/ProductImprovement_Active.svg';
import ProductImprovementStatic from '@assets/images/tip_direct_nav_menu/ProductImprovement_Static.svg';
import ProfileActive from '@assets/images/tip_direct_nav_menu/Profile_Active.svg';
import ProfileStatic from '@assets/images/tip_direct_nav_menu/Profile_Static.svg';
import ResourcesActive from '@assets/images/tip_direct_nav_menu/Resources_Active.svg';
import ResourcesStatic from '@assets/images/tip_direct_nav_menu/Resources_Static.svg';
import ReviewsActive from '@assets/images/tip_direct_nav_menu/Reviews_Active.svg';
import ReviewsStatic from '@assets/images/tip_direct_nav_menu/Reviews_Static.svg';
import SettingsActive from '@assets/images/tip_direct_nav_menu/Settings_Active.svg';
import SettingsStatic from '@assets/images/tip_direct_nav_menu/Settings_Static.svg';
import SetupActive from '@assets/images/tip_direct_nav_menu/Setup.svg';
import SetupStatic from '@assets/images/tip_direct_nav_menu/Setup_Static.svg';
import TipDirectActive from '@assets/images/tip_direct_nav_menu/TipDirect_Active.svg';
import TipDirectStatic from '@assets/images/tip_direct_nav_menu/TipDirect_Static.svg';
import UsersActive from '@assets/images/tip_direct_nav_menu/Users_Active.svg';
import UsersStatic from '@assets/images/tip_direct_nav_menu/Users_Static.svg';
import { SignupType, UserRoles } from '@constants';
import {
  canSeeProductImprovement,
  canSeeSettingsTab,
  isGuestReviewsEnabled,
  isPaymentsCompany,
  isProductEnabled,
  isQuestionnairesEnabledForCompany,
  isTourPhotosEnabledForCompany
} from '@utils/helpers';
// Each menu keeps the message ids it had before, so translations, which are
// keyed by id, render exactly as they did.
import desktopMessages from './TipDirectLeftMenu/messages';
import mobileMessages from './messages';

// The single tab enum. The value doubles as the path segment for most tabs
// (`/tipdirect/${value.toLowerCase()}`); the rest carry an explicit `path`.
// TipDirectLeftMenu and containers/TapToTipPage re-export this object, so
// existing imports keep working and can no longer drift apart.
export const TapToTipTabs = {
  DASHBOARD: 'Dashboard',
  PAYMENTS: 'Payments',
  PROFILE: 'Profile',
  USERS: 'Users',
  REVIEWS: 'Reviews',
  TAPTOTIP: 'TipDirect',
  TOURS: 'Tours',
  RESOURCES: 'Resources',
  BALANCE: 'Balance',
  SETTINGS: 'Settings',
  SETUP: 'Setup',
  MESSAGING: 'Messaging',
  // Card 119 — guide advice, /tipdirect/learn. Labelled "FAQ".
  LEARN: 'Learn',
  // Card 305 — guide-facing tour photo capture, /tipdirect/tourphotos.
  TOURPHOTOS: 'TourPhotos',
  // Card 323 — the guide's guest feedback QR, /tipdirect/feedback.
  FEEDBACK: 'Feedback',
  // Card 324 — head office's questionnaires and alerts, /tipdirect/guestfeedback.
  GUEST_FEEDBACK: 'GuestFeedback',
  // Card 288 — guest review replies and moderation. PROTOTYPE. /tipdirect/guestreviews.
  GUEST_REVIEWS: 'GuestReviews',
  // Card 349 — not a path segment: the route is /tipdirect/product-improvement.
  PRODUCT_IMPROVEMENT: 'ProductImprovement',
  STRIPE: 'Stripe',
  // Legacy ticketing pages, linked from the mobile menu only (see below).
  MANIFEST: 'Manifest',
  BOOKINGS: 'Bookings'
};

export const NavSurface = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile'
};

// Where an entry sits within a menu.
//  MAIN      the TipDirect tab list itself
//  FOOTER    after the list (Settings): below the invite buttons on desktop,
//            and outside the TipDirect-product block on mobile
//  TICKETING the mobile menu's legacy ticketing block, above the TipDirect list
export const NavSection = {
  MAIN: 'main',
  FOOTER: 'footer',
  TICKETING: 'ticketing'
};

const tipDirectPath = name => `/tipdirect/${name.toLowerCase()}`;

const hasStripeConnectCustom = user => !!user?.profile?.stripeConnectCustom?.id;

// The mobile menu's legacy ticketing block: Manifest and Bookings, for
// TicketingSystem companies (never in the US region, see isProductEnabled),
// hidden from guides and payments analysts.
export function showsTicketingBlock(user) {
  return (
    isProductEnabled(SignupType.TicketingSystem, user?.companyDetails?.products) &&
    user.role !== UserRoles.TapToTipAgent &&
    user.role !== UserRoles.paymentsAnalyst
  );
}

/*
 * Every entry, in desktop order.
 *
 *  name         TapToTipTabs value
 *  path         where a click goes
 *  label        { desktop, mobile } message descriptors (or plain strings,
 *               shown untranslated), or (user) => that pair
 *  icons        { desktop: { static, active }, mobile }
 *  section      NavSection (default MAIN)
 *  gate         who sees it, evaluated by isTabVisible:
 *    role                      only this role (usually admin)
 *    excludedRoles             never these roles
 *    requiredPaymentsCompany   payments companies only
 *    hiddenForPaymentsCompany  never at a payments company (card 119)
 *    requires                  (user) => boolean, for company switches and
 *                              other rules that are not plan features
 *    paymentsAnalyst           payments analysts see ONLY entries with this
 *                              set, and see them regardless of the rest of
 *                              the gate (their routes are corralled to
 *                              Payments and Stripe, components/Routes)
 *  mobile / desktop   partial gate overrides for one menu only. None are in
 *               use: card 362 aligned the four that card 354 had kept. A new
 *               one is a decision and the nav parity test pins it.
 *  desktopOnly / mobileOnly   the entry exists on one menu only, on purpose
 *  requiredFeature   plan feature that paints a "Pro" pill on the desktop
 *               rail. It does NOT hide the tab.
 *  mobileInTicketingBlock   on mobile, sits in the ticketing block (with the
 *               messaging alert dot) whenever that block is shown, and in the
 *               TipDirect list otherwise
 */
export const TIPDIRECT_TABS = [
  {
    name: TapToTipTabs.DASHBOARD,
    path: tipDirectPath(TapToTipTabs.DASHBOARD),
    label: { desktop: desktopMessages.dashboard, mobile: mobileMessages.dashboard },
    icons: {
      desktop: { static: DashboardStatic, active: DashboardActive },
      mobile: MobileDashboardIcon
    },
    // Not for payments analysts on either menu: their Dashboard route only
    // redirects them to Payments (card 362 removed the mobile entry).
    gate: {}
  },
  {
    name: TapToTipTabs.TAPTOTIP,
    path: tipDirectPath(TapToTipTabs.TAPTOTIP),
    label: user => {
      if (isPaymentsCompany(user.companyId, user)) {
        return { desktop: desktopMessages.takePayments, mobile: mobileMessages.takePayments };
      }
      if (user.userFeatures?.tipsDisabled ?? user.tipsDisabled) {
        return { desktop: desktopMessages.getReviews, mobile: mobileMessages.getReviews };
      }
      return { desktop: desktopMessages.tipDirect, mobile: mobileMessages.tipdirect };
    },
    icons: {
      desktop: { static: TipDirectStatic, active: TipDirectActive },
      mobile: MobileTipDirectIcon
    },
    gate: {}
  },
  {
    name: TapToTipTabs.BALANCE,
    path: tipDirectPath(TapToTipTabs.BALANCE),
    label: { desktop: desktopMessages.balance, mobile: mobileMessages.balance },
    icons: {
      desktop: { static: BalanceStatic, active: BalanceActive },
      mobile: MobileBalanceIcon
    },
    gate: { requires: hasStripeConnectCustom }
  },
  {
    name: TapToTipTabs.PAYMENTS,
    path: tipDirectPath(TapToTipTabs.PAYMENTS),
    label: { desktop: desktopMessages.payments, mobile: mobileMessages.payments },
    icons: {
      desktop: { static: PaymentsStatic, active: PaymentsActive },
      mobile: MobilePaymentsIcon
    },
    gate: { paymentsAnalyst: true }
  },
  {
    // Card 304 — /tipdirect/setup is adminOnly, so guides are never shown it.
    name: TapToTipTabs.SETUP,
    path: tipDirectPath(TapToTipTabs.SETUP),
    label: { desktop: desktopMessages.setup, mobile: mobileMessages.setup },
    icons: {
      desktop: { static: SetupStatic, active: SetupActive },
      mobile: MobileSetupIcon
    },
    gate: { role: UserRoles.admin }
  },
  {
    name: TapToTipTabs.PROFILE,
    path: tipDirectPath(TapToTipTabs.PROFILE),
    label: { desktop: desktopMessages.profile, mobile: mobileMessages.profile },
    icons: {
      desktop: { static: ProfileStatic, active: ProfileActive },
      mobile: MobileProfileIcon
    },
    gate: {}
  },
  {
    // Desktop-only on purpose: managing the team is not a phone task, and the
    // mobile menu has never had it (Andy, 2026-10-07: keep it that way).
    name: TapToTipTabs.USERS,
    path: tipDirectPath(TapToTipTabs.USERS),
    label: { desktop: desktopMessages.users },
    icons: { desktop: { static: UsersStatic, active: UsersActive } },
    gate: { role: UserRoles.admin },
    desktopOnly: true
  },
  // Guest questionnaires sit directly under Users when switched on (Andy,
  // 2026-10-02): for the payments companies they are for, they are the main
  // thing head office comes here to do.
  {
    // Card 323 — the guide shows guests a feedback QR at the end of a tour.
    // No role (owners guide too) and deliberately NOT hidden for payments
    // companies, unlike its neighbours: they are the first users.
    name: TapToTipTabs.FEEDBACK,
    path: tipDirectPath(TapToTipTabs.FEEDBACK),
    label: { desktop: desktopMessages.feedbackQr, mobile: mobileMessages.feedbackQr },
    icons: {
      desktop: { static: FeedbackQrStatic, active: FeedbackQrActive },
      mobile: MobileFeedbackQrIcon
    },
    gate: { requires: isQuestionnairesEnabledForCompany }
  },
  {
    // Card 324 — head office builds questionnaires and sets alerts here.
    // Admin only: a guide must not read what guests said about their tour.
    name: TapToTipTabs.GUEST_FEEDBACK,
    path: tipDirectPath(TapToTipTabs.GUEST_FEEDBACK),
    label: { desktop: desktopMessages.guestFeedback, mobile: mobileMessages.guestFeedback },
    icons: {
      desktop: { static: GuestFeedbackStatic, active: GuestFeedbackActive },
      mobile: MobileGuestFeedbackIcon
    },
    gate: { role: UserRoles.admin, requires: isQuestionnairesEnabledForCompany }
  },
  {
    name: TapToTipTabs.RESOURCES,
    path: tipDirectPath(TapToTipTabs.RESOURCES),
    label: { desktop: desktopMessages.resources, mobile: mobileMessages.resources },
    icons: {
      desktop: { static: ResourcesStatic, active: ResourcesActive },
      mobile: MobileResourcesIcon
    },
    gate: { role: UserRoles.admin },
    requiredFeature: 'ResourceTippingPoints'
  },
  {
    name: TapToTipTabs.REVIEWS,
    path: tipDirectPath(TapToTipTabs.REVIEWS),
    label: { desktop: desktopMessages.reviews, mobile: mobileMessages.reviews },
    icons: {
      desktop: { static: ReviewsStatic, active: ReviewsActive },
      mobile: MobileReviewsIcon
    },
    gate: {},
    requiredFeature: 'PostTipReviewCollection'
  },
  {
    // Card 288 — guest review replies and moderation. PROTOTYPE.
    //
    // Admin only, and not cosmetic: a guide must not be able to hide a review
    // about themselves, which is why this is a separate tab rather than a
    // section of the guide-visible Reviews tab above. Gated on the company's
    // manual guestReviews switch (not a plan feature, so not requiredFeature,
    // which only draws a pill and would leave the tab clickable).
    name: TapToTipTabs.GUEST_REVIEWS,
    path: tipDirectPath(TapToTipTabs.GUEST_REVIEWS),
    label: { desktop: desktopMessages.guestReviews, mobile: mobileMessages.guestReviews },
    icons: {
      desktop: { static: GuestReviewsStatic, active: GuestReviewsActive },
      mobile: MobileReviewsIcon
    },
    gate: {
      role: UserRoles.admin,
      requires: isGuestReviewsEnabled,
      // Hidden at payments companies on both menus (card 362; the phone
      // menu used to skip this).
      hiddenForPaymentsCompany: true
    }
  },
  {
    // Card 119 — guide advice. No role: guides are the audience and admins
    // keep it too (at small operators the owner also guides). New on mobile
    // with card 354.
    name: TapToTipTabs.LEARN,
    path: tipDirectPath(TapToTipTabs.LEARN),
    label: { desktop: desktopMessages.learn, mobile: desktopMessages.learn },
    icons: {
      desktop: { static: LearnStatic, active: LearnActive },
      mobile: MobileReviewsIcon
    },
    gate: { hiddenForPaymentsCompany: true }
  },
  {
    // Card 305 — taking tour photos is the guide's job, so no role. Gated on
    // the company's manual tourPhotos switch: until an operator is turned on,
    // the tab does not exist for anyone there.
    name: TapToTipTabs.TOURPHOTOS,
    path: tipDirectPath(TapToTipTabs.TOURPHOTOS),
    label: { desktop: desktopMessages.tourPhotos, mobile: mobileMessages.tourPhotos },
    icons: {
      desktop: { static: LearnStatic, active: LearnActive },
      mobile: MobileReviewsIcon
    },
    // Hidden at payments companies on both menus, as Guest Reviews above.
    gate: { requires: isTourPhotosEnabledForCompany, hiddenForPaymentsCompany: true }
  },
  {
    // Card 354 — on mobile this used to exist only inside the ticketing block,
    // so an admin at a TipDirect-only company never saw it on a phone. It now
    // follows the rail's gate everywhere, and still sits in the ticketing block
    // (with its alert dot) for the companies that have one.
    name: TapToTipTabs.MESSAGING,
    path: '/messaging',
    label: { desktop: desktopMessages.messaging, mobile: mobileMessages.messaging },
    icons: {
      desktop: { static: MessagingStatic, active: MessagingActive },
      mobile: MobileReviewsIcon
    },
    gate: { role: UserRoles.admin },
    requiredFeature: 'Messaging',
    mobileInTicketingBlock: true
  },
  {
    // Card 349 — company admins whose company has product credits, plus
    // TripAdmit staff (canSeeProductImprovement). PIP's /auth/sso enforces the
    // same rule server-side, so a typed URL cannot get round it.
    //
    // Production still needs the bring-up in dev/PIP_PRODUCTION_BRINGUP.md
    // (backend connect, region, runner); item 7 there still applies. With no
    // company holding credits in prod, only staff on tripadmit.com addresses
    // would see the tab before that work lands, but they would see it broken.
    name: TapToTipTabs.PRODUCT_IMPROVEMENT,
    path: '/tipdirect/product-improvement',
    label: {
      desktop: desktopMessages.productImprovement,
      mobile: mobileMessages.productImprovement
    },
    icons: {
      desktop: { static: ProductImprovementStatic, active: ProductImprovementActive },
      mobile: MobileReviewsIcon
    },
    gate: { role: UserRoles.admin, requires: canSeeProductImprovement }
  },
  {
    // New on mobile with card 354.
    name: TapToTipTabs.STRIPE,
    path: tipDirectPath(TapToTipTabs.STRIPE),
    label: { desktop: desktopMessages.stripe, mobile: desktopMessages.stripe },
    icons: {
      desktop: { static: SettingsStatic, active: SettingsActive },
      mobile: MobileStripeIcon
    },
    gate: { role: UserRoles.admin, requiredPaymentsCompany: true, paymentsAnalyst: true }
  },
  {
    name: TapToTipTabs.SETTINGS,
    path: '/settings',
    section: NavSection.FOOTER,
    // The mobile entry has always been the untranslated string "Settings".
    label: { desktop: desktopMessages.settings, mobile: 'Settings' },
    icons: {
      desktop: { static: SettingsStatic, active: SettingsActive },
      mobile: MobileSettingsIcon
    },
    // Card 362 — one gate for both menus: admins, plus anyone with their own
    // active Pro subscription. That subscription panel (Manage plan -> Stripe
    // billing portal) is all a guide gets on /settings, and the only place in
    // the app that offers it, so those guides keep the tab, now on the rail
    // too. A guide without one used to get the tab on mobile and an empty page.
    gate: { requires: canSeeSettingsTab }
  },
  {
    // Legacy ticketing pages: a phone has no other way to them, the desktop
    // reaches them from the top bar, not this rail.
    name: TapToTipTabs.MANIFEST,
    path: '/manifest',
    section: NavSection.TICKETING,
    // Untranslated on mobile, as it always was.
    label: { mobile: 'Manifest' },
    icons: { mobile: ManifestIcon },
    gate: { requires: showsTicketingBlock },
    mobileOnly: true
  },
  {
    name: TapToTipTabs.BOOKINGS,
    path: '/bookings',
    section: NavSection.TICKETING,
    label: { mobile: 'Bookings' },
    icons: { mobile: BookingsIcon },
    gate: { requires: showsTicketingBlock },
    mobileOnly: true
  }
];

// Mobile order. The phone menu has always had its own order (Profile second,
// TipDirect near the end), and card 354 keeps it. A MAIN-section entry missing
// from this list is still shown, after these, so forgetting it here can never
// hide a tab again; the nav parity test fails until it is placed on purpose.
export const MOBILE_TAB_ORDER = [
  TapToTipTabs.DASHBOARD,
  TapToTipTabs.PROFILE,
  TapToTipTabs.BALANCE,
  TapToTipTabs.PAYMENTS,
  TapToTipTabs.SETUP,
  TapToTipTabs.FEEDBACK,
  TapToTipTabs.GUEST_FEEDBACK,
  TapToTipTabs.REVIEWS,
  TapToTipTabs.TOURPHOTOS,
  TapToTipTabs.GUEST_REVIEWS,
  TapToTipTabs.LEARN,
  TapToTipTabs.TAPTOTIP,
  TapToTipTabs.RESOURCES,
  TapToTipTabs.MESSAGING,
  TapToTipTabs.PRODUCT_IMPROVEMENT,
  TapToTipTabs.STRIPE
];

export function findTab(name) {
  return TIPDIRECT_TABS.find(tab => tab.name === name);
}

// The gate that applies on one menu: the shared gate plus that menu's override.
export function gateFor(tab, surface) {
  return { ...tab.gate, ...(tab[surface] || {}) };
}

export function isTabVisible(tab, user, surface) {
  if (!user) return false;
  if (tab.desktopOnly && surface !== NavSurface.DESKTOP) return false;
  if (tab.mobileOnly && surface !== NavSurface.MOBILE) return false;

  const gate = gateFor(tab, surface);

  if (user.role === UserRoles.paymentsAnalyst) {
    return gate.paymentsAnalyst === true;
  }

  const userIsPaymentsCompany = isPaymentsCompany(user.companyId, user);

  return (
    (!gate.role || user.role === gate.role) &&
    (!gate.excludedRoles || !gate.excludedRoles.includes(user.role)) &&
    (!gate.requiredPaymentsCompany || userIsPaymentsCompany) &&
    // Card 119 — the inverse of requiredPaymentsCompany, for tabs that are
    // wrong FOR payments companies rather than only meant for them.
    (!gate.hiddenForPaymentsCompany || !userIsPaymentsCompany) &&
    (!gate.requires || Boolean(gate.requires(user)))
  );
}

export function tabLabel(tab, user, surface) {
  const labels = typeof tab.label === 'function' ? tab.label(user) : tab.label;
  return labels[surface];
}

export function formatTabLabel(intl, tab, user, surface) {
  const label = tabLabel(tab, user, surface);
  return typeof label === 'string' ? label : intl.formatMessage(label);
}

function inSection(section) {
  return tab => (tab.section || NavSection.MAIN) === section;
}

function visibleTabs(user, surface) {
  return TIPDIRECT_TABS.filter(tab => isTabVisible(tab, user, surface));
}

function mobileRank(tab) {
  const at = MOBILE_TAB_ORDER.indexOf(tab.name);
  return at === -1 ? MOBILE_TAB_ORDER.length + TIPDIRECT_TABS.indexOf(tab) : at;
}

// What the desktop rail renders: the tab list, then (after the invite
// buttons) the footer.
export function getDesktopMenu(user) {
  const tabs = visibleTabs(user, NavSurface.DESKTOP);
  return {
    main: tabs.filter(inSection(NavSection.MAIN)),
    footer: tabs.filter(inSection(NavSection.FOOTER))
  };
}

// What the mobile menu renders: the ticketing block, the TipDirect list, then
// the footer.
export function getMobileMenu(user) {
  const tabs = visibleTabs(user, NavSurface.MOBILE);
  const ticketingBlock = Boolean(user) && showsTicketingBlock(user);
  const movesToTicketing = tab => tab.mobileInTicketingBlock && ticketingBlock;

  return {
    // Manifest and Bookings first, then anything that moved in (Messaging).
    ticketing: [
      ...tabs.filter(inSection(NavSection.TICKETING)),
      ...tabs.filter(movesToTicketing)
    ],
    main: tabs
      .filter(tab => inSection(NavSection.MAIN)(tab) && !movesToTicketing(tab))
      .sort((a, b) => mobileRank(a) - mobileRank(b)),
    footer: tabs.filter(inSection(NavSection.FOOTER))
  };
}
